import { useSelector } from 'react-redux';
import moment from 'moment';
import { useMemo } from 'react';
import { shouldUseWhatsAppV2 } from '../../modules/Integrations/whatsappRollout';
import {
  isWhatsAppMsPlatform,
  toSidebarPlatformKey
} from '../../../services/inbox/inboxMsChatPlatform';

/** BSP / Gupshup: ventana de atención de 24h (igual que GS legacy). */
const WHATSAPP_CARE_WINDOW_PLATFORMS = new Set([
  'whatsapp_bsp_messages',
  'whatsapp_gupshup_messages'
]);

function isWithinInboundHours(recentInboundMessageDate, hours) {
  if (!recentInboundMessageDate) return false;
  const rDate = moment(recentInboundMessageDate).local();
  return moment().local().diff(rDate, 'hours') < hours;
}

function useChatAvailability({ conversation }) {
  /**
   * Memoiza retailer_info desde Redux para performance visual.
   */
  const retailer_info = useSelector((reduxState) => reduxState.retailerUsersReducer.retailer_info);

  /**
   * Memoiza currentRetailerUser desde Redux para performance visual.
   */
  const currentRetailerUser = useSelector((reduxState) => reduxState.mainReducer.currentRetailerUser);

  /**
   * Multinúmero (inbox MS / WhatsApp V2) vs integración única legacy.
   */
  const isMultiNumber = useMemo(
    () => shouldUseWhatsAppV2(retailer_info),
    [retailer_info]
  );

  const conversationPlatform = conversation?.platform;
  const isQrConversation = conversationPlatform === 'whatsapp_qr_messages'
    || (!isMultiNumber && retailer_info.qr_integrated);

  /**
   * Determina si el retailer tiene balance suficiente o integración QR para enviar mensajes.
   *
   * @description
   * - Si el chat/integración es QR, se considera que siempre tiene "balance" para escribir.
   * - Si el balance de WS es mayor a -10, se considera que tiene balance suficiente.
   */
  const hasBalance = useMemo(
    () => currentRetailerUser.ws_balance > -10 || isQrConversation,
    [currentRetailerUser, isQrConversation]
  );

  /**
   * Determina si el usuario puede escribir mensajes en el chat.
   * @description
   * - Multinúmero: según el subtype del chat (QR siempre; BSP/Gupshup = ventana 24h).
   * - Legacy: QR siempre; Gupshup = ventana 24h.
   */
  const canWriteMessage = useMemo(() => {
    if (isMultiNumber && isWhatsAppMsPlatform(conversationPlatform)) {
      if (conversationPlatform === 'whatsapp_qr_messages') return true;
      if (WHATSAPP_CARE_WINDOW_PLATFORMS.has(conversationPlatform)) {
        return hasBalance
          && isWithinInboundHours(conversation?.recentInboundMessageDate, 24);
      }
      return false;
    }

    if (retailer_info.qr_integrated) return true;
    if (retailer_info.gs_integrated) {
      return hasBalance
        && isWithinInboundHours(conversation?.recentInboundMessageDate, 24);
    }
    return !hasBalance;
  }, [
    isMultiNumber,
    retailer_info,
    conversationPlatform,
    conversation?.recentInboundMessageDate,
    hasBalance
  ]);

  /**
   * Determina si el chat está cerrado por falta de balance o inactividad.
   *
   * @function isChatClosed
   * @memberof useAreaChatWindowUI
   * @returns {boolean} true si el chat está cerrado, false si está abierto.
   *
   * @description
   * - Para WhatsApp (legacy o MS BSP/QR/Gupshup), cerrado si no se puede escribir.
   * - Para Messenger/Instagram (incl. enums MS), cerrado si no hay inbound en 7 días.
   */
  const isChatClosed = useMemo(() => {
    const sidebarPlatform = toSidebarPlatformKey(conversationPlatform);

    if (sidebarPlatform === 'whatsapp' || isWhatsAppMsPlatform(conversationPlatform)) {
      return !canWriteMessage;
    }

    if (['messenger', 'instagram'].includes(sidebarPlatform)) {
      if (!conversation?.recentInboundMessageDate) return true;
      return moment().local().diff(moment(conversation.recentInboundMessageDate).local(), 'days') > 7;
    }

    return false;
  }, [canWriteMessage, conversation?.recentInboundMessageDate, conversationPlatform]);

  /**
   * Mia habilitado a nivel retailer y plataforma de la conversación presente
   * en retailer_mia_platforms. Usa toSidebarPlatformKey para que enums MS
   * (whatsapp_bsp_messages, whatsapp_qr_messages, etc.) matcheen "whatsapp".
   */
  const isMiaPlatformConfigured = useMemo(() => {
    const normalizedPlatform = toSidebarPlatformKey(conversationPlatform)?.toLowerCase();
    if (!normalizedPlatform) return false;

    return !!(
      retailer_info?.chatbot_ai
      && retailer_info?.mia_chatbot_active
      && retailer_info?.retailer_mia_platforms?.some(
        (p) => p.mia_platform_name.toLowerCase() === normalizedPlatform
      )
    );
  }, [
    conversationPlatform,
    retailer_info?.chatbot_ai,
    retailer_info?.mia_chatbot_active,
    retailer_info?.retailer_mia_platforms
  ]);

  const isMiaBlockingComposer = !!(conversation?.hasMia && isMiaPlatformConfigured);

  return {
    hasBalance,
    canWriteMessage,
    isChatClosed,
    isMiaPlatformConfigured,
    isMiaBlockingComposer
  };
}

export default useChatAvailability;
